import { useCallback, useEffect, useRef, useState } from 'react';
import { IconCopy, IconFileTypeTxt, IconPhotoScan, IconSparkles } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import LimitReachedModal from '@/components/common/LimitReachedModal';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { useAuthStore } from '../../../../../store/authStore';
import { useOfficeSpaceStore } from '../../../../../store/officeSpaceStore';
import sohozToolsService from '../../../../../services/sohozToolsService';
import ImageDropZone from '../../components/image/ImageDropZone';
import { ImageFileRow } from '../../components/image/ImageFileRow';
import { formatFileSize } from '../../image/compress/compressImageEngine';
import ToolCard from '../../components/common/ToolCard';
import ToolBoothTabs from '../../components/common/ToolBoothTabs';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import useImagePaste from '../../components/image/useImagePaste';
import useFailureLock from '../../components/common/useFailureLock';
import { copyTextToClipboard, downloadBlob, getToolStatusText } from '../../toolPageUtils';
import {
    AI_SPLIT_ACCEPT as ACCEPT,
    AI_SPLIT_MAX_BYTES as MAX_BYTES,
    fileToDataUrl,
} from '../../image/ai-photo-split/aiPhotoSplitEngine';

// The free reader's output box, verbatim: the two tools show a transcription
// the same way.
const textareaClass = 'min-h-[min(420px,55vh)] w-full resize-y rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 text-[14px] leading-7 text-[#3D3833] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500';

/**
 * Read the writing out of a picture — printed or handwritten, Bangla or English.
 *
 * Ordinary OCR (the Tesseract the job scraper uses) reads print well and
 * handwriting not at all, which is the case that actually turns up: a
 * handwritten application, a filled form, a page of notes. So this one goes
 * through the vision model on the server, and costs one credit a run, spent the
 * moment the button is pressed — a failed run costs the same as a good one.
 *
 * What comes back lands in an editable box: a transcription is a starting point,
 * and fixing one word in place beats retyping the page.
 */
export default function AiImageToTextTool() {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const digits = useCallback((n) => (isBn ? toBnDigits(n) : String(n)), [isBn]);

    const { user } = useAuthStore();
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const isOperator = !!(selectedOfficeSpace && user && selectedOfficeSpace.ownerId != null
        && selectedOfficeSpace.ownerId !== user.id);

    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [idleDragging, setIdleDragging] = useState(false);
    const [creditCost, setCreditCost] = useState(null);
    const [progress, setProgress] = useState(0);
    const [limitOpen, setLimitOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        sohozToolsService
            .getCost(selectedOfficeSpace?.id ? { officeSpaceId: selectedOfficeSpace.id } : {})
            .then((res) => { if (alive) setCreditCost(res?.data?.creditCost ?? null); })
            .catch(() => {});
        return () => { alive = false; };
    }, [selectedOfficeSpace?.id]);

    const reset = useCallback(() => {
        setFile(null);
        setPreview(null);
        setText('');
    }, []);

    const load = useCallback(async (picked) => {
        if (!picked) return;
        if (!picked.type?.startsWith('image/')) {
            notifications.show({ title: t('common.error'), message: t('aiImageToText.errors.type'), color: 'red' });
            return;
        }
        if (picked.size > MAX_BYTES) {
            notifications.show({ title: t('common.error'), message: t('aiImageToText.errors.tooLarge'), color: 'red' });
            return;
        }
        setFile(picked);
        setText('');
        setPreview((old) => {
            if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
            return URL.createObjectURL(picked);
        });
    }, [t]);

    useImagePaste((pastedFiles) => load(pastedFiles?.[0]));

    useEffect(() => () => { if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview); }, [preview]);

    const lock = useFailureLock(3, 60); // 3 failed runs in a row → the run button waits a minute
    const run = useCallback(async () => {
        if (!file || busy || lock.locked) return;
        setBusy(true);
        try {
            const image = await fileToDataUrl(file);
            const res = await sohozToolsService.imageToText({
                image,
                officeSpaceId: selectedOfficeSpace?.id || undefined,
            });
            setText(res?.data?.text || '');
            lock.recordSuccess();
        } catch (e) {
            if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true);
            else {
                lock.recordFailure();
                notifications.show({
                    title: t('common.error'),
                    message: e?.message || t('aiImageToText.errors.failed'),
                    color: 'red',
                });
            }
        } finally {
            setBusy(false);
        }
    }, [file, busy, lock.locked, selectedOfficeSpace?.id, t]);

    // The model answers in one go, so there is no real progress to report. The
    // bar creeps towards 90 and waits there: a moving bar says "still working",
    // which is the honest part, while a number that never changes reads as a
    // hang. It only ever completes when the answer actually arrives.
    useEffect(() => {
        if (!busy) { setProgress(0); return undefined; }
        setProgress(6);
        const id = setInterval(() => {
            setProgress((p) => (p >= 90 ? p : p + Math.max(1, Math.round((90 - p) / 12))));
        }, 700);
        return () => clearInterval(id);
    }, [busy]);

    const copyAll = useCallback(() => {
        if (!text) return;
        copyTextToClipboard(text);
        notifications.show({ message: t('aiImageToText.copied'), color: 'green' });
    }, [text, t]);

    const downloadTxt = useCallback(() => {
        if (!text) return;
        const base = (file?.name || 'text').replace(/\.[^.]+$/, '');
        downloadBlob(new Blob([text], { type: 'text/plain;charset=utf-8' }), `${base}.txt`);
    }, [text, file]);

    const status = busy ? 'processing' : !file ? 'empty' : text ? 'success' : 'ready';
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;

    return (
        <>
            <ToolPageLayout
                slug="text/ai-image-to-text"
                subtitleKey="aiImageToText.subtitle"
                creditCost={creditCost}
                status={status}
                statusText={getToolStatusText(status, t, {
                    empty: t('aiImageToText.status.empty'),
                    ready: t('aiImageToText.status.ready'),
                    success: t('aiImageToText.status.done', { count: digits(words) }),
                })}
                onReset={file ? reset : undefined}
                showTrust={false}
                primaryAction={text ? {
                    label: t('aiImageToText.copy'),
                    onClick: copyAll,
                    icon: IconCopy,
                } : {
                    label: busy ? t('aiImageToText.working') : lock.locked ? t('toolPage.retryLocked', { s: digits(lock.secondsLeft) }) : t('aiImageToText.run'),
                    disabled: lock.locked && !!file,
                    onClick: file ? run : () => fileInputRef.current?.click(),
                    icon: file ? IconSparkles : IconPhotoScan,
                    loading: busy,
                }}
                secondaryAction={text ? {
                    label: t('aiImageToText.downloadTxt'),
                    onClick: downloadTxt,
                    icon: IconFileTypeTxt,
                } : undefined}
                controlsTitle={t('toolPage.files', { count: digits(file ? 1 : 0) })}
                controls={(
                    <ToolBoothTabs onPick={load}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPT}
                            className="hidden"
                            onChange={(e) => { load(e.target.files?.[0]); e.target.value = ''; }}
                        />
                        {!file ? (
                            <ImageDropZone
                                title={t('aiImageToText.dropTitle')}
                                hint={t('aiImageToText.dropHint')}
                                isDragging={idleDragging}
                                onClick={() => fileInputRef.current?.click()}
                                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIdleDragging(true); }}
                                onDragLeave={() => setIdleDragging(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIdleDragging(false);
                                    load(e.dataTransfer?.files?.[0]);
                                }}
                            />
                        ) : (
                            <ImageFileRow
                                name={file.name}
                                meta={formatFileSize(file.size)}
                                onRemove={busy ? undefined : reset}
                                removeLabel={t('aiImageToText.removeFile')}
                            />
                        )}
                    </ToolBoothTabs>
                )}
                settings={(
                    <ToolCard title={t('aiImageToText.howTitle')}>
                        <p className="text-[12.5px] leading-relaxed text-[#A8A099] dark:text-zinc-400">
                            {t('aiImageToText.howBody')}
                        </p>
                        {creditCost != null && (
                            <p className="mt-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-[12px] font-semibold leading-relaxed text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-400">
                                {t('aiImageToText.creditNote', { count: digits(creditCost) })}
                            </p>
                        )}
                        {text ? (
                            <div className="mt-3 flex items-center justify-between border-t border-[#F0EEEB] pt-3 dark:border-zinc-800">
                                <span className="text-[12px] text-[#A8A099] dark:text-zinc-400">
                                    {t('aiImageToText.wordCount', { count: digits(words) })}
                                </span>
                                <button
                                    type="button"
                                    onClick={run}
                                    disabled={busy}
                                    className="text-[12px] font-semibold text-primary transition-colors hover:underline disabled:opacity-40"
                                >
                                    {t('aiImageToText.again')}
                                </button>
                            </div>
                        ) : null}
                    </ToolCard>
                )}
                preview={(
                    <ToolPreviewPanel
                        title={text ? t('aiImageToText.outputTitle') : t('aiImageToText.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        processingLabel={t('aiImageToText.processingProgress', { progress: digits(progress) })}
                        progress={progress}
                        idleTitle={t('aiImageToText.previewEmpty')}
                        idleHint={t('aiImageToText.previewEmptyHint')}
                        showIdle={!file}
                        flushContent={!!text}
                        readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('aiImageToText.run')} /> : null}
                        onIdleClick={() => fileInputRef.current?.click()}
                        onIdleDrop={(e) => {
                            e.preventDefault();
                            setIdleDragging(false);
                            load(e.dataTransfer?.files?.[0]);
                        }}
                        onIdleDragOver={(e) => { e.preventDefault(); setIdleDragging(true); }}
                        onIdleDragLeave={() => setIdleDragging(false)}
                        idleDragging={idleDragging}
                    >
                        {text ? (
                            <div className="flex w-full flex-col gap-2.5">
                                <div className="flex items-center justify-end">
                                    <button
                                        type="button"
                                        onClick={copyAll}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#3D3833] transition-colors hover:border-sky-400/50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                                    >
                                        <IconCopy size={14} stroke={1.9} />
                                        {t('common.copy')}
                                    </button>
                                </div>
                                {/* Editable on purpose: a transcription is a draft,
                                    and fixing one word beats retyping the page. */}
                                <textarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    className={textareaClass}
                                />
                                <p className="text-right text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                    {t('aiImageToText.characterCount', { count: digits(text.length) })}
                                </p>
                            </div>
                        ) : file && preview ? (
                            <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 p-2">
                                <img
                                    src={preview}
                                    alt={file.name}
                                    className="max-h-[min(420px,60vh)] max-w-full rounded-xl border border-[#ECE8E4] object-contain shadow-sm dark:border-zinc-700"
                                />
                                <p className="text-center text-[12px] text-[#A8A099] dark:text-zinc-500">
                                    {file.name}
                                </p>
                            </div>
                        ) : null}
                    </ToolPreviewPanel>
                )}
            />
            <LimitReachedModal opened={limitOpen} onClose={() => setLimitOpen(false)} isOperator={isOperator} />
        </>
    );
}
